"use client"

import Image from "next/image"
import { buttonVariants } from "@/components/ui/button"
import { useLocale } from "next-intl"
import { useTranslations } from "@/lib/intl/use-translations"
import { useCountry } from "@/components/layout/country-provider"
import { careersKeys as k } from "@/i18n/careers-keys"
import { jobSlug, cmsLocale, type JobListing } from "@/lib/cms"
import { cn } from "@/lib/utils"
import { safeUrl } from "@/lib/url"
import { useLocalePath } from "@/lib/use-localized-navigation"
import { FaqSection, type FaqAccordionItem } from "@/components/ui/faq-section"
import { blobMediaUrl } from "@/lib/blob-url"
import { useEditRegion } from "@/lib/edit/edit-region-registry"

/* ───────────────────── Company values data ───────────────────── */

const VALUES = [
  {
    titleKey: k.valueTrustedSidekickTitle,
    descKey: k.valueTrustedSidekickDesc,
  },
  { titleKey: k.valueAlwaysThereTitle, descKey: k.valueAlwaysThereDesc },
  { titleKey: k.valueBetterEveryDayTitle, descKey: k.valueBetterEveryDayDesc },
  {
    titleKey: k.valueActionablyProactiveTitle,
    descKey: k.valueActionablyProactiveDesc,
  },
  { titleKey: k.valueGrittyTitle, descKey: k.valueGrittyDesc },
  { titleKey: k.valueThinkBigTitle, descKey: k.valueThinkBigDesc },
]

/* ───────────────────── Components ───────────────────── */

function LocationIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M12 2C8.7 2 6 4.7 6 8c0 5.3 6 14 6 14s6-8.7 6-14c0-3.3-2.7-6-6-6z" />
      <circle cx="12" cy="8" r="2" />
    </svg>
  )
}

function BriefcaseIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="2" y="7" width="20" height="14" rx="2" />
      <path d="M16 7V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v2" />
    </svg>
  )
}

export function JobCard({
  job,
  index: _index,
  applyLabel,
  detailHref,
  translatedType,
  cmsId,
  editLocale,
}: {
  job: {
    title: string
    department: string
    location: string
    type: string
    href: string
  }
  index: number
  applyLabel: string
  detailHref: string
  translatedType?: string
  /** Payload job-listing doc id — registers the inline-editor region for the
   *  `title` field. Absent → no region. */
  cmsId?: string
  /** BCP47 CMS locale these docs were fetched in (region provenance). */
  editLocale?: string
}) {
  const titleRef = useEditRegion<HTMLHeadingElement>(
    cmsId && editLocale
      ? {
          collection: "job-listings",
          id: cmsId,
          field: "title",
          locale: editLocale,
          kind: "scalar",
        }
      : null
  )
  return (
    <div>
      <div className="flex flex-col justify-between gap-4 rounded-[16px] bg-white p-6 sm:flex-row sm:items-center">
        <div className="flex flex-col gap-2">
          <a href={detailHref} className="group">
            <h3
              ref={titleRef}
              className="type-title-md group-hover:text-aubergine-700 text-neutral-900 transition-colors"
            >
              {job.title}
            </h3>
          </a>
          <div className="type-body-md flex flex-wrap items-center gap-3 text-neutral-500">
            <span className="flex items-center gap-1">
              <BriefcaseIcon />
              {job.department}
            </span>
            <span className="text-neutral-300">·</span>
            <span className="flex items-center gap-1">
              <LocationIcon />
              {job.location}
            </span>
            <span className="text-neutral-300">·</span>
            <span className="rounded-full bg-neutral-100 px-2 py-0.5 text-xs font-medium text-neutral-600">
              {translatedType || job.type}
            </span>
          </div>
        </div>
        <a
          href={detailHref}
          className={cn(
            buttonVariants({ variant: "primary", shape: "round", size: "sm" }),
            "flex-shrink-0 whitespace-nowrap"
          )}
        >
          {applyLabel}
        </a>
      </div>
    </div>
  )
}

/* ───────────────────── Page ───────────────────── */

export default function CareersClient({
  initialJobs,
  initialFaqs,
}: {
  initialJobs: JobListing[]
  initialFaqs: FaqAccordionItem[]
}) {
  const t = useTranslations("careers")
  const lp = useLocalePath()
  const locale = useLocale()
  const editLocale = cmsLocale(locale, useCountry().code)

  const jobs = initialJobs.map((j) => ({
    id: j.id,
    title: j.title,
    department: j.department,
    location: j.location,
    type: j.type,
    href: safeUrl(j.applicationUrl),
    slug: jobSlug(j),
  }))

  return (
    <>
      {/* Hero */}
      <section className="pt-12 pb-12 text-center md:pt-20">
        <div className="mx-auto max-w-[1440px] px-4 md:px-10">
          <p
            className="type-label-md text-foreground flex items-center justify-center gap-2 font-mono font-medium uppercase"
            style={{ letterSpacing: "0.48px" }}
          >
            <span className="bg-foreground inline-block h-[10px] w-[10px] rounded-[2px]" />
            {t(k.pageLabel)}
          </p>
          <h1 className="type-display-lg mx-auto mt-4 max-w-3xl text-balance text-neutral-900">
            {t(k.heading)}
          </h1>
          <p className="type-body-lg mx-auto mt-6 max-w-xl text-neutral-600">
            {t(k.subtitle)}
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <a
              href="#open-roles"
              className={buttonVariants({
                variant: "primary",
                shape: "round",
                size: "default",
              })}
            >
              {t(k.seeOpenRoles)}
            </a>
            <a
              href="https://jobs.needle.so/aheadhealth/6bfb8884-ad8f-4cef-a4c3-83b19e581045?view=application"
              target="_blank"
              rel="noopener noreferrer"
              className={buttonVariants({
                variant: "outline",
                shape: "round",
                size: "default",
              })}
            >
              {t(k.sendOpenApplication)}
            </a>
          </div>
        </div>
      </section>

      {/* Hero image */}
      <section className="pb-16">
        <div className="mx-auto max-w-[1440px] px-4 md:px-10">
          <div
            className="relative overflow-hidden rounded-[16px]"
            style={{ height: 480 }}
          >
            <Image
              src={blobMediaUrl("/images/misc/careers-hero.png")}
              alt={t(k.heroImageAlt)}
              fill
              className="object-cover"
            />
          </div>
        </div>
      </section>

      {/* Open roles */}
      <section id="open-roles" className="py-16">
        <div className="mx-auto max-w-[1440px] px-4 md:px-10">
          <div>
            <p
              className="type-label-md text-foreground flex items-center gap-2 font-mono font-medium uppercase"
              style={{ letterSpacing: "0.48px" }}
            >
              <span className="bg-foreground inline-block h-[10px] w-[10px] rounded-[2px]" />
              {t(k.openRolesLabel)}
            </p>
            <h2 className="type-display-sm mt-4 text-neutral-900">
              {t(k.openRolesHeading)}
            </h2>
            <p className="type-body-lg mt-3 max-w-xl leading-relaxed text-neutral-600">
              {t(k.openRolesBody)}
            </p>
          </div>

          <div className="mt-10 flex flex-col gap-4">
            {jobs.map((job, index) => (
              <JobCard
                key={job.title}
                job={job}
                index={index}
                applyLabel={t(k.applyNow)}
                detailHref={lp(`/careers/${job.slug}`)}
                cmsId={job.id}
                editLocale={editLocale}
                translatedType={
                  job.type === "Full-time"
                    ? t("jobTypeFullTime" as never)
                    : job.type === "Part-time"
                      ? t("jobTypePartTime" as never)
                      : undefined
                }
              />
            ))}
          </div>

          <div>
            <div className="mt-6 text-center">
              <p className="type-body-md text-neutral-500">
                {t(k.dontSeeAFit)}{" "}
                <a
                  href="https://jobs.needle.so/aheadhealth/6bfb8884-ad8f-4cef-a4c3-83b19e581045?view=application"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-aubergine-700 hover:text-aubergine-900 underline underline-offset-2 transition-colors"
                >
                  {t(k.sendAnOpenApplication)}
                </a>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values / culture */}
      <section className="py-16">
        <div className="mx-auto max-w-[1440px] px-4 md:px-10">
          <div>
            <p
              className="type-label-md text-foreground flex items-center gap-2 font-mono font-medium uppercase"
              style={{ letterSpacing: "0.48px" }}
            >
              <span className="bg-foreground inline-block h-[10px] w-[10px] rounded-[2px]" />
              {t(k.howWeWorkLabel)}
            </p>
            <h2 className="type-display-sm mt-4 text-neutral-900">
              {t(k.whatWeBelieveIn)}
            </h2>
            <p className="type-body-lg mt-3 max-w-xl leading-relaxed text-neutral-600">
              {t(k.valuesBody)}
            </p>
          </div>

          <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {VALUES.map((value) => (
              <div key={value.titleKey}>
                <div className="flex flex-col gap-3 rounded-[16px] bg-white p-6">
                  <span className="bg-foreground inline-block h-[10px] w-[10px] rounded-[2px]" />
                  <h3 className="type-title-sm text-neutral-900">
                    {t(value.titleKey)}
                  </h3>
                  <p className="type-body-md leading-[1.5] text-neutral-600">
                    {t(value.descKey)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team / investors CTA */}
      <section className="py-16">
        <div className="mx-auto max-w-[1440px] px-4 md:px-10">
          <div>
            <div className="bg-aubergine-900 flex flex-col items-start gap-8 overflow-hidden rounded-[16px] p-8 md:flex-row md:items-center md:gap-16 md:p-14">
              <div className="flex-1">
                <p
                  className="type-label-md flex items-center gap-2 font-mono font-medium text-white/50 uppercase"
                  style={{ letterSpacing: "0.48px" }}
                >
                  <span className="inline-block h-[10px] w-[10px] rounded-[2px] bg-white/50" />
                  {t(k.backedByLabel)}
                </p>
                <h2 className="type-display-sm mt-4 leading-snug text-white">
                  {t(k.backedByHeading)}
                </h2>
                <p className="mt-4 max-w-lg text-sm leading-relaxed text-white/70">
                  {t(k.backedByBody)}
                </p>
              </div>
              <div className="flex flex-shrink-0 flex-col gap-3">
                <a
                  href="#open-roles"
                  className={cn(
                    buttonVariants({
                      variant: "primary",
                      shape: "round",
                      size: "default",
                    }),
                    "text-aubergine-900 bg-white hover:bg-neutral-100 hover:shadow-none"
                  )}
                >
                  {t(k.seeOpenRoles)}
                </a>
                <a
                  href="https://jobs.needle.so/aheadhealth/6bfb8884-ad8f-4cef-a4c3-83b19e581045?view=application"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={cn(
                    buttonVariants({
                      variant: "outline",
                      shape: "round",
                      size: "default",
                    }),
                    "border-white/40 text-white hover:border-white/40 hover:bg-white/10"
                  )}
                >
                  {t(k.sendOpenApplication)}
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <FaqSection
        heading={t(k.faqHeading)}
        items={initialFaqs}
        linkLabel={null}
        currentPage="careers"
      />
    </>
  )
}
